import { computed, ref, type FunctionalComponent, type MaybeRef, type Ref } from 'vue'
import { EProviderImportStep } from '../types/steps'
import { computedWithControl } from '@vueuse/core'
import { SelectProviderStep } from '../steps/selectProvider'
import { EProviderImportEvent, type UseProviderImport } from './useProviderImport'
import { AuthenticateStep } from '../steps/authenticate'
import type { JSX } from 'vue/jsx-runtime'
import { clamp } from 'math'
import { ConnectingStep } from '../steps/connecting'
import { SelectPositionsStep } from '../steps/selectPositions'
import { StepWrapper } from '../components/step-wrapper'
import { SavingStep } from '../steps/saving'
import { FinsquidFailed } from '../components/finsquid-failed'
import { FinsquidMaintenance } from '../components/finsquid-maintenance'
import type { IProviderImport } from 'findef'

export type UseProviderImportStepsProps = {
  hook: UseProviderImport;
  showCrumbs?: MaybeRef<boolean>;
  title?: MaybeRef<string>;
  selectProviderComponent?: FunctionalComponent<{
    handleSelectProviderImport: (imp: IProviderImport) => any
  }>
}

export type UseProviderImportSteps = {
  step: Ref<EProviderImportStep>
  setStep: (step: EProviderImportStep) => void
  setStepIndex: (index: number) => void
  nextStep: () => void
  previousStep: () => void
  currentContent: Ref<JSX.Element>
  currentIndex: Ref<number>
}

export const useProviderImportSteps = (
  props: UseProviderImportStepsProps
): UseProviderImportSteps => {
  const stepOrder: EProviderImportStep[] = [
    EProviderImportStep.SELECT_PROVIDER,
    EProviderImportStep.AUTHENTICATE,
    EProviderImportStep.CONNECTING,
    EProviderImportStep.SELECT_POSITIONS,
    EProviderImportStep.SAVING
  ]

  const step = ref<EProviderImportStep>(EProviderImportStep.SELECT_PROVIDER)

  const titles = ['Select Provider', 'Authenticate', 'Select Assets & Liabilities']
  const crumbIndex = computed(() => {
    switch (step.value) {
      case EProviderImportStep.SELECT_PROVIDER:
        return 0
      case EProviderImportStep.AUTHENTICATE:
        return 1
      case EProviderImportStep.CONNECTING:
        return 1
      case EProviderImportStep.SELECT_POSITIONS:
        return 2
      case EProviderImportStep.SAVING:
        return 2
      default:
        return 0
    }
  })

  const setStep = (next: EProviderImportStep) => {
    if (step.value === next) return;
    step.value = next
  }

  const setStepIndex = (index: number) => {
    setStep(stepOrder[clamp(index, 0, stepOrder.length - 1)])
  }

  const currentIndex = computed(() => stepOrder.indexOf(step.value))

  const nextStep = () => {
    const cur = stepOrder.indexOf(step.value)
    setStepIndex(cur + 1)
  }
  const previousStep = () => {
    const cur = stepOrder.indexOf(step.value)
    setStepIndex(cur - 1)
  }

  const currentContent = computedWithControl(
    [step as any, () => props.selectProviderComponent as any],
    () => {
      const content = {
        [EProviderImportStep.SELECT_PROVIDER]: () =>
          props.selectProviderComponent ? (
            <props.selectProviderComponent
              handleSelectProviderImport={(imp) => {
                props.hook.selectProvider(imp.providerId)
              }}
            />
          ) : (
            <SelectProviderStep hook={props.hook} />
          ),
        [EProviderImportStep.AUTHENTICATE]: () => <AuthenticateStep hook={props.hook} />,
        [EProviderImportStep.CONNECTING]: () => <ConnectingStep hook={props.hook} />,
        [EProviderImportStep.SELECT_POSITIONS]: () => <SelectPositionsStep hook={props.hook} />,
        [EProviderImportStep.SAVING]: () => <SavingStep hook={props.hook} />,
        [EProviderImportStep.FAILED]: () => <FinsquidFailed importAssets={props.hook} />,
        [EProviderImportStep.MAINTENANCE]: () => <FinsquidMaintenance importAssets={props.hook} />
      }[step.value]()

      return (
        <StepWrapper currentStep={step} title={props.title} crumbs={props.showCrumbs} stepIndex={crumbIndex} titles={titles} hook={props.hook}>
          {content}
        </StepWrapper>
      )
    }
  )

  props.hook.events.subscribe(EProviderImportEvent.FETCHING_USER_ACCOUNTS, () => {
    setStep(EProviderImportStep.CONNECTING)
  });

  props.hook.events.subscribe(EProviderImportEvent.QR_FINISHED, () => {
    console.log('qr finish')
    //setStep(EProviderImportStep.CONNECTING);
  })

  props.hook.events.subscribe(EProviderImportEvent.AUTHENTICATION_FINISHED, async (ev) => {
    console.log('auth finish')
    console.log(ev.accounts)
    setStep(EProviderImportStep.CONNECTING)
    await props.hook.refreshProviderImport()
    setStep(EProviderImportStep.SELECT_POSITIONS)
  })

  props.hook.events.subscribe(EProviderImportEvent.FAILED, () => {
    setStep(EProviderImportStep.FAILED)
  })

  props.hook.events.subscribe(EProviderImportEvent.MAINTENANCE, () => {
    setStep(EProviderImportStep.MAINTENANCE)
  })

  props.hook.events.subscribe(EProviderImportEvent.RESTART, async () => {
    setStep(EProviderImportStep.SELECT_PROVIDER)
    await props.hook.refreshProviderImport()
  })

  return {
    step,
    setStep,
    setStepIndex,
    nextStep,
    previousStep,
    currentContent,
    currentIndex
  }
}
